import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { UserExperience } from '@apply/api/src/api/ApplyApi';
import { experienceQueryKey } from '../keys/experienceQueryKey';

/**
 * Reads and writes a single field on a single Experience record in the React Query cache.
 * Supports creating a new Experience when no experienceId is provided.
 */
export function useExperienceField<T>(
    experienceId: string | undefined,
    selector: (experience: UserExperience) => T,
    updater: (experience: UserExperience, value: T) => UserExperience
): [T | undefined, (value: T) => void] {
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<UserExperience | undefined, Error, T | undefined>({
        queryKey: experienceQueryKey(experienceId),
        queryFn: skipToken,
        select: (exp) => (exp ? selector(exp) : undefined),
    });

    const setValue = (value: T) => {
        queryClient.setQueryData<UserExperience | undefined>(experienceQueryKey(experienceId), (old) => {
            // If no existing record, create a new one
            if (!old) {
                const created: UserExperience = updater({} as UserExperience, value);
                return created;
            }

            // Otherwise update the existing record
            return updater(old, value);
        });
    };

    return [currentValue, setValue];
}
